/**
 * Presentational fallback UI for the error boundary: message, details, Try again, optional Home.
 */
import { Button } from "@material-ui/core";
import { makeThemedStyles } from "../../../viewcomponents/utils";
import { t } from "i18next";
import React from "react";
import { Link } from "react-router-dom";
import type { ErrorFallbackViewProps } from "../types";

const useStyles = makeThemedStyles("ErrorBoundary.Fallback", (theme) => ({
  overlay: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "24px",
    backgroundColor: "rgba(0,0,0,0.04)",
    zIndex: 1300,
  },
  fullpage: {
    marginTop: "70px",
    width: "100%",
    textAlign: "center",
  },
  inline: {
    padding: "24px",
    textAlign: "center",
    border: "1px solid #ccc",
    borderRadius: "8px",
    background: "#fafafa",
    maxWidth: "560px",
    width: "100%",
    boxShadow: "0 4px 20px rgba(0,0,0,0.12)",
  },
  heading: {
    margin: 0,
  },
  headingInline: {
    margin: "1rem 0 0.5rem",
  },
  details: {
    whiteSpace: "pre-wrap",
    textAlign: "left",
    marginTop: "8px",
  },
  actions: {
    marginTop: "16px",
    display: "flex",
    gap: "12px",
    justifyContent: "center",
    flexWrap: "wrap",
  },
}));

export const ErrorFallbackView: React.FC<ErrorFallbackViewProps> = ({
  message,
  componentStack,
  onRetry,
  variant,
  homeTo,
}) => {
  const classes = useStyles();
  const isInline = variant === "inline";
  const containerClass = isInline ? classes.inline : classes.fullpage;
  const headingClass = isInline ? classes.headingInline : classes.heading;

  const content = (
    <div className={containerClass}>
      <h2 className={headingClass}>{t("Something went wrong.")}</h2>
      <details className={classes.details}>
        <summary>{t("Details")}</summary>
        {message}
        <br />
        {componentStack}
      </details>
      <div className={classes.actions}>
        <Button variant="contained" color="primary" onClick={onRetry}>
          {t("Try again")}
        </Button>
        {isInline && (
          <Button component={Link} to={homeTo ?? "/"} variant="outlined">
            {t("Home")}
          </Button>
        )}
      </div>
    </div>
  );

  return isInline ? (
    <div className={classes.overlay}>{content}</div>
  ) : (
    content
  );
};

export default ErrorFallbackView;
